import { useRef, type ReactNode } from "react";
import type { ExtractedSourceDocument } from "@keydials/api-client";
import { builderCopy as copy, fill } from "../copy";
import { DOCUMENT_ACCEPT } from "../describe";

/** The document control's state. Start keeps it, so it survives switching between the ways to start. */
export interface DescribeUpload {
  attached: ExtractedSourceDocument | null;
  /** The name of the file being read, or null when nothing is. */
  reading: string | null;
  error: string | null;
  /** False once the server has said documents can't be added here, so no button offers another. */
  canAdd: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
}

interface DescribeStartProps {
  description: string;
  onDescription: (value: string) => void;
  drafting: boolean;
  /** The document control, or undefined where a document can't seed a draft. */
  upload?: DescribeUpload;
  /** The fields Start asks whichever way the dial begins: its name and purpose, its kind, and its word. */
  children: ReactNode;
  onDraft: () => void;
  onStop: () => void;
}

/**
 * Describe it, on the Start stage (KEY-560): a paragraph about the author's process, a document if they have one, the answers
 * every new dial is asked, and "Draft my dial". While the draft is being made the author can stop waiting; nothing is created
 * unless it finishes. The description is always needed, document or not.
 */
export function DescribeStart({ description, onDescription, drafting, upload, children, onDraft, onStop }: DescribeStartProps) {
  const reading = upload?.reading != null;
  return (
    <>
      <label className="kd-builder-field">
        <span>{copy.start.describe.fieldLabel}</span>
        <textarea
          rows={6}
          value={description}
          disabled={drafting || reading}
          placeholder={copy.start.describe.fieldPlaceholder}
          onChange={(e) => onDescription(e.target.value)}
        />
      </label>
      {upload && <DocumentField upload={upload} disabled={drafting} />}
      {children}
      <div className="kd-builder-footer kd-describe-footer">
        {drafting ? (
          <>
            <span className="kd-builder-muted" role="status">
              {upload?.attached ? copy.start.describe.draftingFromDocument : copy.start.describe.drafting}
            </span>
            <button type="button" className="kd-btn" onClick={onStop}>
              {copy.start.describe.stop}
            </button>
          </>
        ) : (
          <button type="button" className="kd-btn-primary" disabled={!description.trim() || reading} onClick={onDraft}>
            {copy.start.describe.draft}
          </button>
        )}
      </div>
    </>
  );
}

function DocumentField({ upload, disabled }: { upload: DescribeUpload; disabled: boolean }) {
  const picker = useRef<HTMLInputElement>(null);
  const words = copy.start.describe.document;
  const { attached, reading, error, canAdd, onPick, onRemove } = upload;
  const busy = disabled || reading !== null;
  const choose = () => picker.current?.click();

  return (
    <div className="kd-describe-document">
      <input
        ref={picker}
        type="file"
        accept={DOCUMENT_ACCEPT}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared, so picking the same file again after removing it still reads it.
          e.target.value = "";
          if (file) onPick(file);
        }}
      />
      {reading !== null ? (
        <p className="kd-builder-muted" role="status">
          {fill(words.reading, { name: reading })}
        </p>
      ) : attached ? (
        <div className="kd-describe-attached">
          <div className="kd-describe-attached-text">
            <span className="kd-describe-file">{attached.fileName}</span>
            {attached.truncated && <span className="kd-builder-muted">{words.trimmed}</span>}
          </div>
          <div className="kd-describe-attached-actions">
            <button type="button" className="kd-btn" disabled={busy || !canAdd} onClick={choose}>
              {words.replace}
            </button>
            <button type="button" className="kd-btn" disabled={busy} onClick={onRemove}>
              {words.remove}
            </button>
          </div>
        </div>
      ) : (
        canAdd && (
          <>
            <button type="button" className="kd-btn" disabled={busy} onClick={choose}>
              {words.add}
            </button>
            <p className="kd-builder-muted">{words.hint}</p>
          </>
        )
      )}
      {error && (
        <p className="kd-builder-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
